import { useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2, Save, Download, FileText, ClipboardList, Star } from "lucide-react";
import { LANGS } from "@/lib/langs";
import { pollJob } from "@/lib/pollJob";
import { CvEditor } from "./tailor/CvEditor";

const MODES = [
  { value: "conservative", label: "Conservative", desc: "Minimal edits, keep original wording." },
  { value: "balanced", label: "Balanced", desc: "Rewrite & reorder to match the job." },
  { value: "aggressive", label: "Aggressive", desc: "Maximise keyword & relevance match." },
];

function ProgressRing({ value }) {
  const r = 52;
  const c = 2 * Math.PI * r;
  const off = c - (Math.min(100, value) / 100) * c;
  return (
    <div className="relative h-32 w-32" data-testid="tailor-progress-ring">
      <svg className="h-32 w-32 -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#e4e4e7" strokeWidth="10" />
        <circle cx="60" cy="60" r={r} fill="none" stroke="#1a3060" strokeWidth="10" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-heading text-2xl font-bold text-[#1a3060]">{Math.round(Math.min(100, value))}%</span>
      </div>
    </div>
  );
}

export default function TailorCV() {
  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [jobUrl, setJobUrl] = useState("");
  const [jobDesc, setJobDesc] = useState("");
  const [mode, setMode] = useState("balanced");
  const [genCover, setGenCover] = useState(true);
  const [language, setLanguage] = useState("en");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [savingTracker, setSavingTracker] = useState(false);
  const [savedToTracker, setSavedToTracker] = useState(false);

  const [content, setContent] = useState(null);
  const [alignment, setAlignment] = useState(null);
  const [coverLetter, setCoverLetter] = useState("");
  const [savedIds, setSavedIds] = useState(null);
  const [progress, setProgress] = useState(0);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const firstRun = searchParams.get("first") === "1";
  const jobUrlRef = useRef(null);

  useEffect(() => {
    if (firstRun && jobUrlRef.current) jobUrlRef.current.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const p = location.state?.prefill;
    if (p) {
      if (p.job_title) setJobTitle(p.job_title);
      if (p.company) setCompany(p.company);
      if (p.job_description) setJobDesc(p.job_description);
      if (p.job_url) setJobUrl(p.job_url);
      toast.success("Prefilled from application");
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setMode(data.tailoring_mode || "balanced");
      setGenCover(data.generate_cover_letter !== false);
      setLanguage(data.preferred_language || "en");
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!loading) { setProgress(0); return; }
    setProgress(6);
    const id = setInterval(() => {
      setProgress((p) => (p >= 95 ? 95 : p + Math.max(1, Math.round((95 - p) / 12))));
    }, 700);
    return () => clearInterval(id);
  }, [loading]);

  const rid = () => Math.random().toString(36).slice(2);

  const fetchFromUrl = async () => {
    if (!jobUrl.trim()) { toast.error("Enter a job URL first"); return; }
    setFetching(true);
    try {
      const { data } = await api.post("/jobs/fetch-url", { url: jobUrl.trim() });
      if (data.job_description) setJobDesc(data.job_description);
      if (data.job_title && !jobTitle.trim()) setJobTitle(data.job_title);
      if (data.company && !company.trim()) setCompany(data.company);
      toast.success("Fetched job description from URL");
    } catch (e) {
      toast.error(formatApiError(e, "Could not fetch the job description"));
    } finally {
      setFetching(false);
    }
  };

  const generate = async () => {
    if (!jobDesc.trim() && !jobUrl.trim()) { toast.error("Add a job description or URL"); return; }
    setLoading(true);
    setSavedIds(null);
    setSavedToTracker(false);
    setAlignment(null);
    try {
      const { data: job } = await api.post("/tailoring/generate", {
        job_description: jobDesc, job_url: jobUrl, job_title: jobTitle,
        company, tailoring_mode: mode, generate_cover_letter: genCover, language,
      });
      const data = await pollJob(job.job_id, "/tailoring/jobs");
      const cv = data.tailored_cv || {};
      setContent({
        title: cv.title || "",
        summary: cv.summary || "",
        career_highlights: cv.career_highlights || [],
        skills: cv.skills || [],
        experience: (cv.experience || []).map((e) => ({
          role: e.role || "", company: e.company || "", location: e.location || "",
          dates: e.dates || "", bullets: e.bullets || (e.description ? [e.description] : []), _k: rid(),
        })),
        education: (cv.education || []).map((e) => ({ degree: e.degree || "", institution: e.institution || "", dates: e.dates || "", _k: rid() })),
        professional_development: cv.professional_development || [],
      });
      setAlignment(data.alignment || null);
      setCoverLetter(data.cover_letter_text || "");
      toast.success("Tailored CV generated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const setField = (k, v) => setContent((c) => ({ ...c, [k]: v }));
  const setLines = (k, v) => setContent((c) => ({ ...c, [k]: v.split("\n") }));
  const updateExp = (i, k, v) => setContent((c) => {
    const experience = [...c.experience]; experience[i] = { ...experience[i], [k]: v }; return { ...c, experience };
  });
  const updateEdu = (i, k, v) => setContent((c) => {
    const education = [...c.education]; education[i] = { ...education[i], [k]: v }; return { ...c, education };
  });
  const removeExp = (i) => setContent((c) => ({ ...c, experience: c.experience.filter((_, x) => x !== i) }));
  const removeEdu = (i) => setContent((c) => ({ ...c, education: c.education.filter((_, x) => x !== i) }));
  const addExp = () => setContent((c) => ({ ...c, experience: [...c.experience, { _k: rid(), company: "", role: "", location: "", dates: "", bullets: [] }] }));
  const addEdu = () => setContent((c) => ({ ...c, education: [...c.education, { _k: rid(), institution: "", degree: "", dates: "" }] }));

  const save = async () => {
    if (!company.trim() || !jobTitle.trim()) { toast.error("Add job title and company to save"); return; }
    setSaving(true);
    try {
      const cleanContent = {
        title: content.title,
        summary: content.summary,
        career_highlights: (content.career_highlights || []).filter(Boolean),
        skills: content.skills,
        experience: content.experience.map(({ _k, ...rest }) => ({ ...rest, bullets: (rest.bullets || []).filter(Boolean) })),
        education: content.education.map(({ _k, ...rest }) => rest),
        professional_development: (content.professional_development || []).filter(Boolean),
      };
      const { data } = await api.post("/tailored-cvs", {
        job_title: jobTitle, company, tailoring_mode: mode,
        content: cleanContent, cover_letter_text: genCover ? coverLetter : null,
        alignment: alignment,
      });
      setSavedIds(data);
      toast.success("Tailored CV saved. Available in Tracker.");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const saveToTracker = async () => {
    if (!savedIds?.tailored_cv_id) { toast.error("Save the tailored CV first"); return; }
    setSavingTracker(true);
    try {
      await api.post("/applications", {
        company, job_title: jobTitle, job_url: jobUrl || null,
        job_description: jobDesc || null, status: "applied",
        tailored_cv_id: savedIds.tailored_cv_id,
        cover_letter_id: savedIds.cover_letter_id || null,
      });
      setSavedToTracker(true);
      toast.success("Added to your Application Tracker");
    } catch (e) {
      toast.error(formatApiError(e, "Could not add to tracker"));
    } finally {
      setSavingTracker(false);
    }
  };

  const downloadCoverText = () => {
    if (savedIds?.cover_letter_id) {
      window.open(`${API}/cover-letters/${savedIds.cover_letter_id}/download`, "_blank");
      return;
    }
    toast.message("Save the tailored CV first to download a styled PDF.");
    const blob = new Blob([coverLetter], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `cover_letter_${company || "job"}.txt`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="tailor-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Tailor CV</h1>
        <p className="text-muted-foreground mt-1 text-sm">Generate a tailored CV & cover letter for a specific job.</p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Inputs */}
        <div className="col-span-12 lg:col-span-4 space-y-4">
          {firstRun && (
            <div className="bg-primary text-primary-foreground rounded-xl p-4" data-testid="tailor-firstrun">
              <p className="font-heading font-bold text-sm flex items-center gap-1.5"><Sparkles className="h-4 w-4" strokeWidth={1.75} /> Create your first tailored CV</p>
              <p className="text-xs text-primary-foreground/80 mt-1.5 leading-relaxed">Just paste a job link (or the description) below and hit <strong>Generate</strong>. We'll use your profile as the base CV and rewrite it to match the role — takes about 20 seconds.</p>
            </div>
          )}
          <Card className="border-zinc-200 shadow-sm">
            <CardHeader><CardTitle className="font-heading text-xl">Job Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Job Title</Label>
                <Input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="Senior Engineer" data-testid="tailor-jobtitle-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Company</Label>
                <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme Inc." data-testid="tailor-company-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Job URL (optional)</Label>
                <div className="flex gap-2">
                  <Input ref={jobUrlRef} value={jobUrl} onChange={(e) => setJobUrl(e.target.value)} placeholder="https://…" data-testid="tailor-joburl-input" />
                  <Button type="button" variant="outline" onClick={fetchFromUrl} disabled={fetching} className="shrink-0" data-testid="tailor-fetch-url-button">
                    {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    <span className="ml-1.5 hidden sm:inline">Fetch</span>
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Job Description</Label>
                <Textarea rows={8} value={jobDesc} onChange={(e) => setJobDesc(e.target.value)} placeholder="Paste the job description here…" data-testid="tailor-jobdesc-input" />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider">Tailoring Mode</Label>
                <RadioGroup value={mode} onValueChange={setMode} className="space-y-2">
                  {MODES.map((m) => (
                    <label key={m.value} htmlFor={`mode-${m.value}`} data-testid={`mode-${m.value}`}
                      className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors ${mode === m.value ? "border-primary bg-zinc-50" : "border-zinc-200 hover:bg-zinc-50"}`}>
                      <RadioGroupItem value={m.value} id={`mode-${m.value}`} className="mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">{m.label}</p>
                        <p className="text-xs text-muted-foreground">{m.desc}</p>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Output Language</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger data-testid="tailor-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LANGS.map((l) => <SelectItem key={l.value} value={l.value} data-testid={`tailor-lang-${l.value}`}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Overrides your Settings language for this CV only.</p>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={genCover} onCheckedChange={(v) => setGenCover(!!v)} data-testid="tailor-cover-checkbox" />
                <span className="text-sm">Generate cover letter</span>
              </label>

              <Button onClick={generate} disabled={loading} data-testid="generate-button" className="w-full h-11 active:scale-[0.98] transition-transform">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating…</> : <><Sparkles className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Generate Tailored CV</>}
              </Button>
              <p className="text-xs text-muted-foreground text-center">This can take 15–30 seconds while the AI writes your CV.</p>
            </CardContent>
          </Card>
        </div>

        {/* Output */}
        <div className="col-span-12 lg:col-span-8">
          {loading && !content && (
            <div className="h-full min-h-[400px] rounded-md border border-dashed border-zinc-300 bg-white flex flex-col items-center justify-center text-center p-8" data-testid="tailor-progress">
              <ProgressRing value={progress} />
              <p className="font-medium mt-4">Tailoring your CV…</p>
              <p className="text-sm text-muted-foreground mt-1">Writing and aligning your CV to the role. This takes 15–30 seconds.</p>
            </div>
          )}

          {!content && !loading && (
            <div className="h-full min-h-[400px] rounded-md border border-dashed border-zinc-300 bg-white flex flex-col items-center justify-center text-center p-8" data-testid="tailor-empty">
              <FileText className="h-10 w-10 text-zinc-300 mb-3" strokeWidth={1.25} />
              <p className="font-medium">Your tailored CV appears here</p>
              <p className="text-sm text-muted-foreground mt-1">Fill in the job details and click Generate.</p>
            </div>
          )}

          {content && (
            <div className="space-y-5" data-testid="tailor-output">
              <div className="flex flex-wrap items-center gap-2 justify-between">
                {alignment ? (
                  <div className="flex items-center gap-2" data-testid="alignment-badge">
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold ${
                      alignment.score >= alignment.target_low ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
                    }`}>
                      <Star className="h-4 w-4 mr-1.5 text-gold fill-gold" strokeWidth={1.5} />
                      {alignment.score}% job match
                    </span>
                    <span className="text-xs text-muted-foreground capitalize">{alignment.mode} target {alignment.target_low}–{alignment.target_high}%</span>
                  </div>
                ) : <span />}
                <div className="flex flex-wrap items-center gap-2">
                  <Button onClick={save} disabled={saving} data-testid="save-tailored-button" className="active:scale-[0.98] transition-transform">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Save Tailored CV</>}
                  </Button>
                  {savedIds && (
                    <a href={`${API}/tailored-cvs/${savedIds.tailored_cv_id}/download`} target="_blank" rel="noreferrer" data-testid="download-tailored-link">
                      <Button variant="outline" className="active:scale-[0.98] transition-transform"><Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Download CV (PDF)</Button>
                    </a>
                  )}
                  {savedIds && (
                    <Button variant="outline" onClick={saveToTracker} disabled={savingTracker || savedToTracker} data-testid="save-to-tracker-button" className="active:scale-[0.98] transition-transform">
                      {savingTracker ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ClipboardList className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> {savedToTracker ? "Added to Tracker" : "Save to Tracker"}</>}
                    </Button>
                  )}
                </div>
              </div>

              {alignment && alignment.missing?.length > 0 && (
                <div className="text-xs text-muted-foreground bg-zinc-50 border border-zinc-200 rounded-md p-3" data-testid="alignment-missing">
                  <span className="font-medium text-foreground">Not yet reflected from the job:</span> {alignment.missing.join(", ")}
                </div>
              )}

              {/* Paper — matches the 2-page A4 template */}
              <CvEditor
                content={content}
                setField={setField}
                setLines={setLines}
                updateExp={updateExp}
                updateEdu={updateEdu}
                removeExp={removeExp}
                removeEdu={removeEdu}
                addExp={addExp}
                addEdu={addEdu}
              />

              {genCover && (
                <Card className="border-zinc-200 shadow-sm max-w-3xl mx-auto">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0">
                    <CardTitle className="font-heading text-xl">Cover Letter</CardTitle>
                    <Button variant="outline" size="sm" onClick={downloadCoverText} data-testid="download-cover-button"><Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Download {savedIds?.cover_letter_id ? "(PDF)" : "(TXT)"}</Button>
                  </CardHeader>
                  <CardContent>
                    <Textarea rows={12} value={coverLetter} onChange={(e) => setCoverLetter(e.target.value)} data-testid="output-cover-letter" />
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
